/**
 * Anonymous game events (DECISIONS 2026-09-29 "Game analytics"). Shared by the app (src/game/track.ts)
 * and the Worker (worker/events.ts), which validates them and writes them to Cloudflare Analytics Engine.
 *
 * No IDs, no cookies, no IP or user agent. The only thing read from the device beyond the event itself is
 * the week the device was first seen (`c`) and whether it was also active yesterday / earlier this week,
 * which is what return rates need. Everything is validated against the closed lists below; anything else is dropped.
 */
import { MAX_HINTS, MAX_SCORE_BOUND, WHO_CLUES } from "./scoring";
import type { Level, PuzzleSet, RoundType } from "../puzzles/types";

export const EVENT_NAMES = ["active", "start", "round", "finish", "share", "challenge", "backup", "restore", "load_error", "vault_open"] as const;
export type EventName = (typeof EVENT_NAMES)[number];

const LEVELS: readonly Level[] = ["easy", "medium", "hard"];
/** Round types in events. The four retired on 2026-09-30 are still accepted from browsers running an older copy. */
const ROUND_TYPES: readonly string[] = ["pin", "next", "who", "order", "odd", "realfic", "place"] satisfies readonly (RoundType | "order" | "odd" | "realfic" | "place")[];
export const VIAS = ["direct", "challenge", "restore"] as const;
/** How a result was shared: WhatsApp or Telegram (since 30 Sep; `sms` briefly the same day), or the old copy / system share sheet. */
export const METHODS = ["sms", "whatsapp", "telegram", "copy", "native"] as const;
export const OUTCOMES = ["win", "lose", "tie"] as const;
export const DEVICES = ["phone", "tablet", "desktop"] as const;
/** How a day is played: today's puzzle, a past day in the Vault, or (Phase C) a Theme mix run. */
export const MODES = ["daily", "vault", "mix"] as const;
/** Where the Vault was opened from. */
export const FROMS = ["summary", "header", "challenge"] as const;

/** Short keys keep beacons tiny. */
export interface GameEvent {
  e: EventName;
  /** level */
  l?: Level;
  /** day number */
  d?: number;
  /** round 1–3 */
  r?: number;
  /** round type */
  t?: RoundType;
  /** score: round points, or the day total on "finish" (from 2026-09-29: small numbers, e.g. 0–20) */
  s?: number;
  /** most points possible: the round's on "round", the day's on "finish" */
  x?: number;
  /** "Whose steps?" named at place 1–5, 0 = not named */
  n?: number;
  /** played streak (days) */
  k?: number;
  /** cohort: Monday (YYYY-MM-DD) of the week this device was first seen */
  c?: string;
  v?: (typeof VIAS)[number];
  m?: (typeof METHODS)[number];
  o?: (typeof OUTCOMES)[number];
  w?: (typeof DEVICES)[number];
  /** first time this device is seen (0/1) */
  nw?: number;
  /** first activity of this device in the current week (0/1) */
  fw?: number;
  /** device was also active yesterday (0/1) */
  py?: number;
  /** success flag (restore) */
  ok?: number;
  /** mode on start / round / finish / share (absent = daily, as before 2026-09-29) */
  md?: (typeof MODES)[number];
  /** vault_open: where it was opened from */
  f?: (typeof FROMS)[number];
  /** active: this device played in the Vault in the last 7 days (0/1) */
  v7?: number;
  /** round (Pin it): hints used, 0–2 (DECISIONS 2026-10-03) */
  h?: number;
  /** round ("Whose steps?"): wrong guesses, 0–5 (skips aren't counted) */
  g?: number;
}

const DATE = /^\d{4}-\d{2}-\d{2}$/;
const int = (v: unknown, min: number, max: number): number | undefined =>
  typeof v === "number" && Number.isInteger(v) && v >= min && v <= max ? v : undefined;
const oneOf = <T extends string>(v: unknown, list: readonly T[]): T | undefined => (typeof v === "string" && (list as readonly string[]).includes(v) ? (v as T) : undefined);

/** Strict parse of an untrusted event. Unknown keys are ignored; an invalid value drops the whole event. */
export function parseEvent(raw: unknown): GameEvent | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const e = oneOf(o.e, EVENT_NAMES);
  if (!e) return null;
  const ev: GameEvent = { e };
  const checks: [keyof GameEvent, unknown, unknown][] = [
    ["l", o.l, oneOf(o.l, LEVELS)],
    ["d", o.d, int(o.d, 1, 100000)],
    ["r", o.r, int(o.r, 1, 3)],
    ["t", o.t, oneOf(o.t, ROUND_TYPES)],
    ["s", o.s, int(o.s, 0, MAX_SCORE_BOUND)],
    ["x", o.x, int(o.x, 1, MAX_SCORE_BOUND)],
    ["n", o.n, int(o.n, 0, 5)],
    ["k", o.k, int(o.k, 0, 100000)],
    ["c", o.c, typeof o.c === "string" && DATE.test(o.c) ? o.c : undefined],
    ["v", o.v, oneOf(o.v, VIAS)],
    ["m", o.m, oneOf(o.m, METHODS)],
    ["o", o.o, oneOf(o.o, OUTCOMES)],
    ["w", o.w, oneOf(o.w, DEVICES)],
    ["nw", o.nw, int(o.nw, 0, 1)],
    ["fw", o.fw, int(o.fw, 0, 1)],
    ["py", o.py, int(o.py, 0, 1)],
    ["ok", o.ok, int(o.ok, 0, 1)],
    ["md", o.md, oneOf(o.md, MODES)],
    ["f", o.f, oneOf(o.f, FROMS)],
    ["v7", o.v7, int(o.v7, 0, 1)],
    ["h", o.h, int(o.h, 0, MAX_HINTS)],
    ["g", o.g, int(o.g, 0, WHO_CLUES)],
  ];
  for (const [key, given, valid] of checks) {
    if (given === undefined) continue;
    if (valid === undefined) return null;
    (ev as unknown as Record<string, unknown>)[key] = valid;
  }
  if (ev.s != null && ev.x != null && ev.s > ev.x) return null;
  return ev;
}

export const setOf = (l?: Level): PuzzleSet | "" => (l ? (l === "easy" ? "family" : "adult") : "");

/**
 * Analytics Engine row layout — the dashboard's queries depend on it, so change both together.
 * blob1 event · blob2 level · blob3 set · blob4 round type · blob5 cohort week · blob6 via · blob7 share method ·
 * blob8 challenge outcome · blob9 device · blob10 country (from Cloudflare, not the device) ·
 * blob11 mode ("" in rows before the Vault = daily) · blob12 where the Vault was opened
 * double1 day · double2 round · double3 score · double4 named at · double5 streak · double6 new · double7 first in week ·
 * double8 active yesterday · double9 ok · double10 most points possible (0 in rows before 2026-09-29) ·
 * double11 played in the Vault in the last 7 days · double12 Pin it hints used · double13 "Whose steps?" wrong guesses
 *   (double12/13 from 2026-10-03; 0 in older rows and in rounds without them, so read them only where blob4 is the type)
 * Layout name: "hints13" (was "vault11", before that "double10").
 */
export function toDataPoint(ev: GameEvent, country: string): { indexes: string[]; blobs: string[]; doubles: number[] } {
  return {
    indexes: [ev.e],
    blobs: [ev.e, ev.l ?? "", setOf(ev.l), ev.t ?? "", ev.c ?? "", ev.v ?? "", ev.m ?? "", ev.o ?? "", ev.w ?? "", /^[A-Z]{2}$/.test(country) ? country : "", ev.md ?? "", ev.f ?? ""],
    doubles: [ev.d ?? 0, ev.r ?? 0, ev.s ?? 0, ev.n ?? 0, ev.k ?? 0, ev.nw ?? 0, ev.fw ?? 0, ev.py ?? 0, ev.ok ?? 0, ev.x ?? 0, ev.v7 ?? 0, ev.h ?? 0, ev.g ?? 0],
  };
}

// ---------- activity (once per device per local day) ----------

export interface Seen {
  /** first local date this device was seen */
  first: string;
  /** last local date an "active" event was sent */
  last: string;
}

const addDays = (date: string, n: number): string => {
  const t = Date.parse(`${date}T00:00:00Z`) + n * 86_400_000;
  return new Date(t).toISOString().slice(0, 10);
};

/** Monday of the week containing `date` (YYYY-MM-DD). */
export function mondayOf(date: string): string {
  const dow = new Date(`${date}T00:00:00Z`).getUTCDay(); // 0 = Sunday
  return addDays(date, -((dow + 6) % 7));
}

/** The "active" event for today, and what to store — or null if today's was already sent. */
export function activity(seen: Seen | null, today: string, vaultRecent: 0 | 1 = 0): { event: GameEvent; next: Seen } | null {
  if (seen && seen.last === today) return null;
  const first = seen?.first && DATE.test(seen.first) ? seen.first : today;
  const event: GameEvent = {
    e: "active",
    c: mondayOf(first),
    nw: seen ? 0 : 1,
    py: seen?.last === addDays(today, -1) ? 1 : 0,
    fw: !seen || seen.last < mondayOf(today) ? 1 : 0,
    v7: vaultRecent,
  };
  return { event, next: { first, last: today } };
}
